import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Search, 
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  CreditCard,
  Banknote,
  Smartphone,
  Receipt,
  PlayCircle,
  StopCircle,
  Printer,
  X,
  Package,
  RefreshCw,
  CheckCircle2,
  Bluetooth,
  Loader2,
  BarChart3,
  RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import ZReadingDialog from './pos/ZReadingDialog';
import RefundDialog, { RefundSuccessDialog } from './pos/RefundDialog';
import PrinterSetupDialog from './pos/PrinterSetupDialog';
import ProductSelectDialog from './pos/ProductSelectDialog';
import ShiftReportDialog from './pos/ShiftReportDialog';
import {
  PRINTER_CONFIG, ESC_POS, textToBytes, formatLine, centerText,
  dashedLine, formatReceiptCurrency, getWholesaleUnit, getRetailUnit
} from './pos/posUtils';

const API = process.env.REACT_APP_BACKEND_URL;

const NASPOS = () => {
  const { user } = useAuth();
  const searchRef = useRef(null);
  
  // State
  const [currentShift, setCurrentShift] = useState(null);
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialogs
  const [showOpenShiftDialog, setShowOpenShiftDialog] = useState(false);
  const [showCloseShiftDialog, setShowCloseShiftDialog] = useState(false);
  const [showCheckoutDialog, setShowCheckoutDialog] = useState(false);
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);
  const [showShiftReportDialog, setShowShiftReportDialog] = useState(false);
  const [showProductSelectDialog, setShowProductSelectDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productSelectForm, setProductSelectForm] = useState({
    saleType: 'wholesale',
    quantity: 1,
    weightKg: '' // For typing actual weight in kg
  });
  
  // Forms
  const [openShiftForm, setOpenShiftForm] = useState({ openingCash: '', notes: '' });
  const [closeShiftForm, setCloseShiftForm] = useState({ closingCash: '', notes: '' });
  const [checkoutForm, setCheckoutForm] = useState({
    paymentMethod: 'cash',
    amountReceived: '',
    customerName: 'Walk-in',
    discount: 0,
    notes: ''
  });
  
  const [lastReceipt, setLastReceipt] = useState(null);
  const [shiftReport, setShiftReport] = useState(null);
  const [shouldAutoPrint, setShouldAutoPrint] = useState(false);
  const [showPrinterSetupDialog, setShowPrinterSetupDialog] = useState(false);
  const [showZReadingDialog, setShowZReadingDialog] = useState(false);
  const [zReadingData, setZReadingData] = useState(null);
  const [zReadingDate, setZReadingDate] = useState(new Date().toISOString().split('T')[0]);
  const [loadingZReading, setLoadingZReading] = useState(false);
  
  // Refund/Exchange state
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [refundSearchTerm, setRefundSearchTerm] = useState('');
  const [refundOriginalSale, setRefundOriginalSale] = useState(null);
  const [refundItems, setRefundItems] = useState([]);
  const [refundType, setRefundType] = useState('refund'); // 'refund' or 'exchange'
  const [refundReason, setRefundReason] = useState('');
  const [loadingRefund, setLoadingRefund] = useState(false);
  const [showRefundSuccess, setShowRefundSuccess] = useState(false);
  const [lastRefund, setLastRefund] = useState(null);
  const [recentRefunds, setRecentRefunds] = useState([]);
  
  // Printer settings from localStorage
  const [printerSettings, setPrinterSettings] = useState(() => {
    const saved = localStorage.getItem('nas_pos_printer_settings');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return { printerName: PRINTER_CONFIG.model, isConfigured: false };
      }
    }
    return { printerName: PRINTER_CONFIG.model, isConfigured: false };
  });

  // Save printer settings to localStorage
  const savePrinterSettings = (settings) => {
    const newSettings = { ...printerSettings, ...settings };
    setPrinterSettings(newSettings);
    localStorage.setItem('nas_pos_printer_settings', JSON.stringify(newSettings));
    toast.success('Printer settings saved!');
  };

  // Bluetooth Printer State
  const [bluetoothDevice, setBluetoothDevice] = useState(null);
  const [bluetoothCharacteristic, setBluetoothCharacteristic] = useState(null);
  const [isBluetoothConnected, setIsBluetoothConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  // Check if Web Bluetooth is supported
  const isBluetoothSupported = typeof navigator !== 'undefined' && 'bluetooth' in navigator;

  // Connect to Bluetooth Printer
  const connectBluetoothPrinter = async () => {
    if (!isBluetoothSupported) {
      toast.error('Bluetooth not supported on this device/browser');
      return;
    }

    setIsConnecting(true);
    try {
      // Request Bluetooth device - try multiple service UUIDs
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: [
          PRINTER_CONFIG.bluetoothServiceUUID,
          PRINTER_CONFIG.altServiceUUID,
          '000018f0-0000-1000-8000-00805f9b34fb',
          '49535343-fe7d-4ae5-8fa9-9fafd205e455',
          'e7810a71-73ae-499d-8c15-faa9aef0c3f2'
        ]
      });

      toast.info(`Connecting to ${device.name || 'printer'}...`);
      
      // Connect to GATT server
      const server = await device.gatt.connect();
      
      // Try to find the printer service
      let service = null;
      let characteristic = null;

      const serviceUUIDs = [
        PRINTER_CONFIG.bluetoothServiceUUID,
        PRINTER_CONFIG.altServiceUUID,
        '000018f0-0000-1000-8000-00805f9b34fb',
        '49535343-fe7d-4ae5-8fa9-9fafd205e455',
        'e7810a71-73ae-499d-8c15-faa9aef0c3f2'
      ];

      for (const uuid of serviceUUIDs) {
        try {
          service = await server.getPrimaryService(uuid);
          if (service) break;
        } catch {
          continue;
        }
      }

      if (!service) {
        // Try to get all services
        const services = await server.getPrimaryServices();
        if (services.length > 0) {
          service = services[0];
        }
      }

      if (!service) {
        throw new Error('Could not find printer service');
      }

      // Get characteristic for writing
      const characteristics = await service.getCharacteristics();
      for (const char of characteristics) {
        if (char.properties.write || char.properties.writeWithoutResponse) {
          characteristic = char;
          break;
        }
      }

      if (!characteristic) {
        throw new Error('Could not find writable characteristic');
      }

      // Handle disconnection
      device.addEventListener('gattserverdisconnected', () => {
        setIsBluetoothConnected(false);
        setBluetoothDevice(null);
        setBluetoothCharacteristic(null);
        toast.warning('Printer disconnected');
      });

      setBluetoothDevice(device);
      setBluetoothCharacteristic(characteristic);
      setIsBluetoothConnected(true);
      
      // Save printer name
      savePrinterSettings({ 
        printerName: device.name || PRINTER_CONFIG.model, 
        isConfigured: true,
        bluetoothId: device.id
      });

      toast.success(`Connected to ${device.name || 'Bluetooth Printer'}!`);
    } catch (error) {
      console.error('Bluetooth connection error:', error);
      if (error.name === 'NotFoundError') {
        toast.error('No printer selected or connection cancelled');
      } else {
        toast.error(`Connection failed: ${error.message}`);
      }
    } finally {
      setIsConnecting(false);
    }
  };

  // Disconnect Bluetooth Printer
  const disconnectBluetoothPrinter = () => {
    if (bluetoothDevice && bluetoothDevice.gatt.connected) {
      bluetoothDevice.gatt.disconnect();
    }
    setIsBluetoothConnected(false);
    setBluetoothDevice(null);
    setBluetoothCharacteristic(null);
    toast.info('Printer disconnected');
  };

  // Generate ESC/POS receipt data
  const generateReceiptData = (receipt) => {
    const commands = [];
    
    // Initialize printer
    commands.push(...ESC_POS.INIT);
    
    // Header - Store name (centered, bold, double height)
    commands.push(...ESC_POS.ALIGN_CENTER);
    commands.push(...ESC_POS.BOLD_ON);
    commands.push(...ESC_POS.DOUBLE_HEIGHT_ON);
    commands.push(...textToBytes("Nenen's Agrivet"));
    commands.push(...ESC_POS.LINE_FEED);
    commands.push(...ESC_POS.NORMAL);
    commands.push(...textToBytes("Supply"));
    commands.push(...ESC_POS.LINE_FEED);
    commands.push(...ESC_POS.BOLD_OFF);
    
    // Receipt number and date
    commands.push(...textToBytes(receipt.receiptNumber || ''));
    commands.push(...ESC_POS.LINE_FEED);
    const date = new Date(receipt.createdAt);
    commands.push(...textToBytes(date.toLocaleString('en-PH', { 
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true 
    })));
    commands.push(...ESC_POS.LINE_FEED);
    
    // Divider
    commands.push(...ESC_POS.ALIGN_LEFT);
    commands.push(...textToBytes(dashedLine()));
    commands.push(...ESC_POS.LINE_FEED);
    
    // Items
    for (const item of (receipt.items || [])) {
      const itemName = (item.productName || 'Item').substring(0, 20);
      const qty = `x${item.quantity} ${item.unit || ''}`;
      commands.push(...textToBytes(itemName));
      commands.push(...ESC_POS.LINE_FEED);
      commands.push(...textToBytes(formatLine(`  ${qty}`, formatReceiptCurrency(item.totalPrice))));
      commands.push(...ESC_POS.LINE_FEED);
    }
    
    // Divider
    commands.push(...textToBytes(dashedLine()));
    commands.push(...ESC_POS.LINE_FEED);
    
    // Subtotal
    commands.push(...textToBytes(formatLine('Subtotal', formatReceiptCurrency(receipt.subtotal))));
    commands.push(...ESC_POS.LINE_FEED);
    
    // Discount (if any)
    if (receipt.discount > 0) {
      commands.push(...textToBytes(formatLine('Discount', `-${formatReceiptCurrency(receipt.discount)}`)));
      commands.push(...ESC_POS.LINE_FEED);
    }
    
    // Total (bold)
    commands.push(...ESC_POS.BOLD_ON);
    commands.push(...ESC_POS.DOUBLE_HEIGHT_ON);
    commands.push(...textToBytes(formatLine('TOTAL', formatReceiptCurrency(receipt.totalAmount))));
    commands.push(...ESC_POS.LINE_FEED);
    commands.push(...ESC_POS.NORMAL);
    commands.push(...ESC_POS.BOLD_OFF);
    
    // Payment details
    if (receipt.paymentMethod === 'cash') {
      commands.push(...textToBytes(formatLine('Cash', formatReceiptCurrency(receipt.amountReceived))));
      commands.push(...ESC_POS.LINE_FEED);
      commands.push(...textToBytes(formatLine('Change', formatReceiptCurrency(receipt.change))));
      commands.push(...ESC_POS.LINE_FEED);
    }
    
    // Divider
    commands.push(...textToBytes(dashedLine()));
    commands.push(...ESC_POS.LINE_FEED);
    
    // Footer
    commands.push(...ESC_POS.ALIGN_CENTER);
    commands.push(...textToBytes(`Payment: ${(receipt.paymentMethod || 'cash').toUpperCase()}`));
    commands.push(...ESC_POS.LINE_FEED);
    commands.push(...textToBytes(`Cashier: ${receipt.cashierName || ''}`));
    commands.push(...ESC_POS.LINE_FEED);
    commands.push(...ESC_POS.LINE_FEED);
    commands.push(...textToBytes('Thank you for your'));
    commands.push(...ESC_POS.LINE_FEED);
    commands.push(...textToBytes('purchase!'));
    commands.push(...ESC_POS.LINE_FEED);
    commands.push(...ESC_POS.LINE_FEED);
    
    // Disclaimer
    commands.push(...textToBytes(dashedLine()));
    commands.push(...ESC_POS.LINE_FEED);
    commands.push(...ESC_POS.BOLD_ON);
    commands.push(...textToBytes('NOT AN OFFICIAL RECEIPT'));
    commands.push(...ESC_POS.BOLD_OFF);
    commands.push(...ESC_POS.LINE_FEED);
    commands.push(...textToBytes('For inventory purposes only'));
    commands.push(...ESC_POS.LINE_FEED);
    commands.push(...textToBytes('For Official Receipt,'));
    commands.push(...ESC_POS.LINE_FEED);
    commands.push(...textToBytes('please speak to our staff'));
    commands.push(...ESC_POS.LINE_FEED);
    commands.push(...ESC_POS.LINE_FEED);
    
    // Add cut indicator line for manual cutting
    commands.push(...textToBytes('- - - - - CUT HERE - - - - -'));
    commands.push(...ESC_POS.LINE_FEED);
    commands.push(...ESC_POS.LINE_FEED);
    commands.push(...ESC_POS.LINE_FEED);
    commands.push(...ESC_POS.LINE_FEED);
    commands.push(...ESC_POS.LINE_FEED);
    commands.push(...ESC_POS.LINE_FEED);
    
    // Feed and cut
    commands.push(...ESC_POS.FEED_AND_CUT);
    
    return new Uint8Array(commands);
  };

  // Print via Bluetooth
  const printViaBluetooth = async (receipt) => {
    if (!isBluetoothConnected || !bluetoothCharacteristic) {
      toast.error('Printer not connected. Please connect first.');
      return false;
    }

    setIsPrinting(true);
    try {
      const receiptData = generateReceiptData(receipt);
      
      // Send data in smaller chunks for thermal printers (128 bytes for stability)
      const chunkSize = 128;
      for (let i = 0; i < receiptData.length; i += chunkSize) {
        const chunk = receiptData.slice(i, i + chunkSize);
        if (bluetoothCharacteristic.properties.writeWithoutResponse) {
          await bluetoothCharacteristic.writeValueWithoutResponse(chunk);
        } else {
          await bluetoothCharacteristic.writeValue(chunk);
        }
        // Longer delay between chunks for thermal printer to process
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      toast.success('Receipt printed!');
      return true;
    } catch (error) {
      console.error('Print error:', error);
      toast.error(`Print failed: ${error.message}`);
      return false;
    } finally {
      setIsPrinting(false);
    }
  };

  // Access check - Admin, explicitly authorized, or default users
  const username = user?.username?.toLowerCase() || '';
  const fullName = user?.full_name?.toLowerCase() || '';
  // Admin check - multiple ways to identify admin
  const isAdmin = user?.role === 'admin' || 
                  user?.role === 'Admin' || 
                  username === 'arbe' || 
                  fullName.includes('administrator') ||
                  user?.is_admin === true;
  const hasExplicitAccess = user?.pos_access === true;
  // Default authorized cashiers + admin usernames
  const isDefaultAuthorized = ['geraldine', 'cristel', 'kristine', 'nicole', 'arbe', 'pabs', 'alma'].some(n => username.includes(n) || fullName.includes(n));
  const canUsePOS = isAdmin || hasExplicitAccess || isDefaultAuthorized;

  useEffect(() => {
    loadCurrentShift();
    loadProducts();
  }, []);

  // Auto-print when receipt dialog opens with autoPrint flag
  useEffect(() => {
    if (showReceiptDialog && shouldAutoPrint && lastReceipt) {
      // Small delay to ensure dialog is rendered
      const timer = setTimeout(async () => {
        // Try Bluetooth first if connected
        if (isBluetoothConnected) {
          await printViaBluetooth(lastReceipt);
        } else {
          printReceipt();
        }
        setShouldAutoPrint(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [showReceiptDialog, shouldAutoPrint, lastReceipt, isBluetoothConnected]);

  useEffect(() => {
    // Search when 1 or more characters typed
    if (searchTerm.length >= 1) {
      // First try local filter
      const filtered = products.filter(p => 
        p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.category?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      
      console.log(`Search "${searchTerm}": Found ${filtered.length} from ${products.length} products`);
      
      if (filtered.length > 0) {
        setSearchResults(filtered.slice(0, 20));
      } else {
        // If no local results, try API search
        const searchAPI = async () => {
          try {
            const res = await axios.get(`${API}/api/nas/pos/products?search=${encodeURIComponent(searchTerm)}`);
            console.log(`API search "${searchTerm}": Found ${res.data?.length || 0} products`);
            setSearchResults(res.data || []);
          } catch (error) {
            console.error('API search error:', error);
            setSearchResults([]);
          }
        };
        searchAPI();
      }
    } else {
      setSearchResults([]);
    }
  }, [searchTerm, products]);

  const loadCurrentShift = async () => {
    try {
      const res = await axios.get(`${API}/api/nas/pos/shift/current`);
      setCurrentShift(res.data);
    } catch (error) {
      console.error('Error loading shift:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const res = await axios.get(`${API}/api/nas/pos/products`);
      console.log('Loaded POS products:', res.data?.length || 0, 'products');
      setProducts(res.data || []);
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Failed to load products');
    }
  };

  // Load Z-Reading (Daily Sales Report)
  const loadZReading = async (date = zReadingDate) => {
    setLoadingZReading(true);
    try {
      const res = await axios.get(`${API}/api/nas/pos/z-reading?date=${date}`);
      setZReadingData(res.data);
    } catch (error) {
      console.error('Error loading Z-Reading:', error);
      toast.error('Failed to load Z-Reading report');
      setZReadingData(null);
    } finally {
      setLoadingZReading(false);
    }
  };

  // Search for sale by receipt number for refund
  const searchSaleForRefund = async () => {
    if (!refundSearchTerm.trim()) {
      toast.error('Please enter a receipt number');
      return;
    }
    
    setLoadingRefund(true);
    try {
      const res = await axios.get(`${API}/api/nas/pos/sale/${encodeURIComponent(refundSearchTerm.trim())}`);
      setRefundOriginalSale(res.data);
      // Initialize refund items with 0 quantity for each item
      setRefundItems(
        (res.data.items || []).map(item => ({
          ...item,
          returnQuantity: 0,
          maxReturn: item.remainingQuantity || item.quantity
        }))
      );
      toast.success('Sale found!');
    } catch (error) {
      console.error('Error finding sale:', error);
      toast.error(error.response?.data?.detail || 'Sale not found');
      setRefundOriginalSale(null);
      setRefundItems([]);
    } finally {
      setLoadingRefund(false);
    }
  };

  // Process the refund
  const processRefund = async () => {
    const itemsToReturn = refundItems.filter(item => item.returnQuantity > 0);
    
    if (itemsToReturn.length === 0) {
      toast.error('Please select at least one item to return');
      return;
    }
    
    const refundAmount = itemsToReturn.reduce((sum, item) => 
      sum + (item.returnQuantity * item.unitPrice), 0
    );
    
    setLoadingRefund(true);
    try {
      const res = await axios.post(`${API}/api/nas/pos/refund`, {
        originalReceiptNumber: refundOriginalSale.receiptNumber,
        items: itemsToReturn.map(item => ({
          productId: item.productId,
          productName: item.productName,
          quantity: item.returnQuantity,
          unit: item.unit,
          unitPrice: item.unitPrice,
          totalPrice: item.returnQuantity * item.unitPrice,
          saleType: item.saleType
        })),
        refundType: refundType,
        refundAmount: refundAmount,
        reason: refundReason,
        notes: `${refundType === 'exchange' ? 'Exchange' : 'Refund'} processed`
      });
      
      setLastRefund(res.data);
      setShowRefundDialog(false);
      setShowRefundSuccess(true);
      
      // Reset refund state
      setRefundSearchTerm('');
      setRefundOriginalSale(null);
      setRefundItems([]);
      setRefundReason('');
      setRefundType('refund');
      
      // Refresh recent refunds
      loadRecentRefunds();
      
      toast.success(`${refundType === 'exchange' ? 'Exchange' : 'Refund'} processed successfully!`);
    } catch (error) {
      console.error('Error processing refund:', error);
      toast.error(error.response?.data?.detail || 'Failed to process refund');
    } finally {
      setLoadingRefund(false);
    }
  };

  // Update return quantity for an item
  const updateRefundItemQuantity = (index, quantity) => {
    const newItems = [...refundItems];
    const maxReturn = newItems[index].maxReturn;
    newItems[index].returnQuantity = Math.min(Math.max(0, quantity), maxReturn);
    setRefundItems(newItems);
  };

  // Load recent refunds (today's)
  const loadRecentRefunds = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await axios.get(`${API}/api/nas/pos/refunds?date_from=${today}`);
      setRecentRefunds(res.data || []);
    } catch (error) {
      console.error('Error loading recent refunds:', error);
    }
  };

  // Load recent refunds on component mount
  useEffect(() => {
    loadRecentRefunds();
    // eslint-disable-next-line
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(amount || 0);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-PH', { 
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  // Shift Management
  const handleOpenShift = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/api/nas/pos/shift/open`, {
        openingCash: parseFloat(openShiftForm.openingCash) || 0,
        notes: openShiftForm.notes
      });
      setCurrentShift(res.data);
      setShowOpenShiftDialog(false);
      setOpenShiftForm({ openingCash: '', notes: '' });
      toast.success('Shift opened successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open shift');
    }
  };

  const handleCloseShift = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/api/nas/pos/shift/${currentShift.id}/close`, {
        closingCash: parseFloat(closeShiftForm.closingCash) || 0,
        notes: closeShiftForm.notes
      });
      setShiftReport(res.data);
      setShowCloseShiftDialog(false);
      setShowShiftReportDialog(true);
      setCurrentShift(null);
      setCart([]);
      setCloseShiftForm({ closingCash: '', notes: '' });
      toast.success('Shift closed successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close shift');
    }
  };

  // Cart Management
  const addToCart = (product, saleType = 'retail', quantity = 1) => {
    console.log('addToCart called:', { productName: product?.name, saleType, quantity });
    
    // Check if shift is open
    if (!currentShift) {
      toast.error('Please open a shift first');
      return;
    }
    
    // Validate product object
    if (!product || !product.id) {
      console.error('Invalid product:', product);
      toast.error('Invalid product data');
      return;
    }
    
    // Handle weight-based sale types (for Feeds)
    const isWeightBased = saleType === 'retail_weight' || saleType === 'wholesale_weight';
    
    // Check stock availability - half_sack and wholesale_weight use wholesale stock
    const availableStock = (saleType === 'wholesale' || saleType === 'half_sack' || saleType === 'wholesale_weight')
      ? (product.wholesaleStock || 0) 
      : (product.retailStock || 0);
    
    // For half_sack, available is double (2 half sacks per full sack)
    // For weight-based wholesale, convert sacks to kg
    let effectiveAvailableStock;
    if (saleType === 'half_sack') {
      effectiveAvailableStock = availableStock * 2;
    } else if (saleType === 'wholesale_weight') {
      // Convert sacks to kg for weight-based wholesale
      const conversionRate = product.conversionRate || 50;
      effectiveAvailableStock = availableStock * conversionRate;
    } else {
      effectiveAvailableStock = availableStock;
    }
    
    if (effectiveAvailableStock <= 0) {
      toast.error(`${product.name} is out of stock for ${saleType} sale`);
      return;
    }
    
    if (quantity > effectiveAvailableStock) {
      toast.error(`Only ${effectiveAvailableStock} available in stock`);
      return;
    }
    
    // Determine unit and price based on sale type
    let unit, price, displayQty;
    if (saleType === 'wholesale') {
      unit = getWholesaleUnit(product).singular;
      price = product.wholesalePrice || product.sellingPricePerSack || 0;
      displayQty = quantity;
    } else if (saleType === 'half_sack') {
      unit = '½ sack';
      price = (product.wholesalePrice || product.sellingPricePerSack || 0) / 2;
      displayQty = quantity;
    } else if (saleType === 'wholesale_weight') {
      // Weight-based wholesale: price is prorated by weight
      unit = 'kg';
      const conversionRate = product.conversionRate || 50;
      const wholesalePrice = product.wholesalePrice || product.sellingPricePerSack || 0;
      // Price per kg = wholesale price / conversion rate
      price = wholesalePrice / conversionRate;
      displayQty = quantity; // quantity is in kg
    } else if (saleType === 'retail_weight') {
      // Weight-based retail: price per kg
      unit = 'kg';
      price = product.retailPrice || product.sellingPricePerKg || 0;
      displayQty = quantity; // quantity is in kg
    } else {
      unit = getRetailUnit(product).singular;
      price = product.retailPrice || product.sellingPricePerKg || 0;
      displayQty = quantity;
    }
    
    // For weight-based sales, include the pricing type in the display
    const displayUnit = isWeightBased 
      ? `kg (${saleType === 'wholesale_weight' ? 'W' : 'R'})` 
      : unit;
    
    const existingIndex = cart.findIndex(item => item.productId === product.id && item.unit === displayUnit);
    
    if (existingIndex >= 0) {
      const newQty = cart[existingIndex].quantity + displayQty;
      if (newQty > effectiveAvailableStock) {
        toast.error(`Only ${effectiveAvailableStock} ${unit}s available in stock`);
        return;
      }
      const newCart = [...cart];
      newCart[existingIndex].quantity = newQty;
      newCart[existingIndex].totalPrice = newQty * newCart[existingIndex].unitPrice;
      setCart(newCart);
      toast.success(`Added ${displayQty} ${unit}(s) of ${product.name}`);
    } else {
      setCart([...cart, {
        productId: product.id,
        productName: product.name,
        quantity: displayQty,
        unit: displayUnit,
        saleType: saleType,
        unitPrice: price,
        totalPrice: price * displayQty,
        availableStock: effectiveAvailableStock
      }]);
      toast.success(`${product.name} added to cart`);
    }
    
    setSearchTerm('');
    setSearchResults([]);
    searchRef.current?.focus();
  };
  
  // Open product select dialog
  const openProductSelect = (product) => {
    if (!currentShift) {
      toast.error('Please open a shift first');
      return;
    }
    
    const hasWholesale = (product.wholesaleStock || 0) > 0;
    const hasRetail = (product.retailStock || 0) > 0;
    
    if (!hasWholesale && !hasRetail) {
      toast.error('This product is out of stock');
      return;
    }
    
    setSelectedProduct(product);
    
    // For Feeds category, reset with weight mode
    const isFeeds = product.category?.toLowerCase().includes('feed');
    setProductSelectForm({
      saleType: hasWholesale ? 'wholesale' : 'retail',
      quantity: 1,
      weightKg: isFeeds ? '' : ''
    });
    setShowProductSelectDialog(true);
  };
  
  // Handle add from product select dialog
  const handleProductSelectAdd = () => {
    if (selectedProduct) {
      const isFeeds = selectedProduct.category?.toLowerCase().includes('feed');
      const isWeightBased = productSelectForm.saleType === 'retail_weight' || productSelectForm.saleType === 'wholesale_weight';
      
      // For Feeds with weight input, use the weight as quantity
      if (isFeeds && productSelectForm.weightKg && parseFloat(productSelectForm.weightKg) > 0) {
        const weight = parseFloat(productSelectForm.weightKg);
        addToCart(selectedProduct, productSelectForm.saleType, weight);
      } else if (!isFeeds || !isWeightBased) {
        // Non-Feeds or traditional sale types
        addToCart(selectedProduct, productSelectForm.saleType, productSelectForm.quantity);
      } else {
        toast.error('Please enter a valid weight');
        return;
      }
      
      setShowProductSelectDialog(false);
      setSelectedProduct(null);
    }
  };

  const updateCartItem = (index, field, value) => {
    const newCart = [...cart];
    
    // Check stock when updating quantity
    if (field === 'quantity') {
      const availableStock = newCart[index].availableStock || 999;
      if (value > availableStock) {
        toast.error(`Only ${availableStock} ${newCart[index].unit}s available`);
        value = availableStock;
      }
    }
    
    newCart[index][field] = value;
    
    if (field === 'quantity' || field === 'unitPrice') {
      newCart[index].totalPrice = newCart[index].quantity * newCart[index].unitPrice;
    }
    
    setCart(newCart);
  };

  const removeFromCart = (index) => {
    setCart(cart.filter((_, i) => i !== index));
  };

  const clearCart = () => {
    if (window.confirm('Clear all items from cart?')) {
      setCart([]);
    }
  };

  // Calculations
  const subtotal = cart.reduce((sum, item) => sum + item.totalPrice, 0);
  const discount = parseFloat(checkoutForm.discount) || 0;
  const total = subtotal - discount;
  const amountReceived = parseFloat(checkoutForm.amountReceived) || 0;
  const change = amountReceived - total;

  // Checkout
  const handleCheckout = async (autoPrint = false) => {
    const discount = parseFloat(checkoutForm.discount) || 0;
    const total = subtotal - discount;
    const amountReceived = parseFloat(checkoutForm.amountReceived) || 0;
    const change = amountReceived - total;

    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    if (checkoutForm.paymentMethod === 'cash' && amountReceived < total) {
      toast.error('Insufficient payment amount');
      return;
    }

    try {
      const res = await axios.post(`${API}/api/nas/pos/sale`, {
        shiftId: currentShift.id,
        items: cart,
        subtotal,
        discount,
        totalAmount: total,
        paymentMethod: checkoutForm.paymentMethod,
        amountReceived: checkoutForm.paymentMethod === 'cash' ? amountReceived : total,
        change: checkoutForm.paymentMethod === 'cash' ? change : 0,
        customerName: checkoutForm.customerName,
        notes: checkoutForm.notes
      });

      setLastReceipt(res.data);
      setShowCheckoutDialog(false);
      setCart([]);
      setCheckoutForm({
        paymentMethod: 'cash',
        amountReceived: '',
        customerName: 'Walk-in',
        discount: 0,
        notes: ''
      });
      
      // Refresh shift data
      loadCurrentShift();
      
      if (autoPrint) {
        toast.success('Sale saved! Printing receipt...');
        setShouldAutoPrint(true);
      } else {
        toast.success('Sale saved successfully!');
        setShouldAutoPrint(false);
      }
      
      // Show receipt dialog
      setShowReceiptDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete sale');
    }
  };

  const printReceipt = () => {
    // Create a print-specific window optimized for Kprinter JK-5802H (58mm thermal)
    const printContent = document.getElementById('receipt');
    if (!printContent) {
      window.print();
      return;
    }

    const printWindow = window.open('', '_blank', 'width=300,height=600');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Receipt - ${PRINTER_CONFIG.brand} ${PRINTER_CONFIG.model}</title>
        <style>
          /* Kprinter JK-5802H Thermal Printer Styles - 58mm paper */
          @page {
            size: 58mm auto;
            margin: 0;
          }
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: 'Courier New', Courier, monospace;
            font-size: 12px;
            line-height: 1.3;
            width: 48mm;
            padding: 2mm;
            color: #000;
          }
          .receipt-header {
            text-align: center;
            border-bottom: 1px dashed #000;
            padding-bottom: 4px;
            margin-bottom: 4px;
          }
          .receipt-header h3 {
            font-size: 14px;
            font-weight: bold;
            margin-bottom: 2px;
          }
          .receipt-header p {
            font-size: 10px;
          }
          .receipt-items {
            margin: 4px 0;
          }
          .receipt-item {
            display: flex;
            justify-content: space-between;
            font-size: 11px;
            margin: 2px 0;
          }
          .receipt-item-name {
            flex: 1;
            word-break: break-word;
          }
          .receipt-item-price {
            text-align: right;
            min-width: 50px;
          }
          .receipt-totals {
            border-top: 1px dashed #000;
            padding-top: 4px;
            margin-top: 4px;
          }
          .receipt-row {
            display: flex;
            justify-content: space-between;
            font-size: 11px;
            margin: 2px 0;
          }
          .receipt-total {
            font-size: 14px;
            font-weight: bold;
          }
          .receipt-footer {
            text-align: center;
            border-top: 1px dashed #000;
            padding-top: 4px;
            margin-top: 4px;
            font-size: 10px;
          }
          .receipt-disclaimer {
            text-align: center;
            border-top: 1px dashed #000;
            padding-top: 4px;
            margin-top: 6px;
            font-size: 9px;
          }
          .receipt-disclaimer p {
            margin: 1px 0;
          }
          .receipt-disclaimer .disclaimer-title {
            font-weight: bold;
            font-size: 10px;
          }
          .printer-info {
            font-size: 8px;
            color: #666;
            margin-top: 8px;
            text-align: center;
          }
        </style>
      </head>
      <body>
        ${printContent.innerHTML}
        <div class="printer-info">
          Printed on ${PRINTER_CONFIG.brand} ${PRINTER_CONFIG.model}
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  if (!canUsePOS) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Package size={48} className="mx-auto text-slate-400 mb-4" />
          <h2 className="text-xl font-semibold text-slate-700">Access Denied</h2>
          <p className="text-slate-500">Only authorized cashiers can use the POS system.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  // No shift open
  if (!currentShift) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <PlayCircle size={64} className="mx-auto text-emerald-500 mb-4" />
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Start Your Shift</h2>
            <p className="text-slate-600 mb-6">Open a new shift to start processing sales.</p>
            <Button 
              onClick={() => setShowOpenShiftDialog(true)}
              className="bg-emerald-600 hover:bg-emerald-700 w-full"
              size="lg"
            >
              <PlayCircle size={20} className="mr-2" />
              Open Shift
            </Button>
            
            {/* Z-Reading Button - Available even without open shift */}
            <div className="mt-4 pt-4 border-t">
              <Button 
                onClick={() => {
                  setZReadingDate(new Date().toISOString().split('T')[0]);
                  loadZReading(new Date().toISOString().split('T')[0]);
                  setShowZReadingDialog(true);
                }}
                variant="outline"
                className="w-full"
              >
                <BarChart3 size={18} className="mr-2" />
                View Daily Sales (Z-Reading)
              </Button>
            </div>
            
            {/* Returns Button - Available even without open shift */}
            <div className="mt-2">
              <Button 
                onClick={() => {
                  setShowRefundDialog(true);
                  setRefundSearchTerm('');
                  setRefundOriginalSale(null);
                  setRefundItems([]);
                  setRefundReason('');
                  setRefundType('refund');
                }}
                variant="outline"
                className="w-full border-amber-300 text-amber-600 hover:bg-amber-50"
              >
                <RotateCcw size={18} className="mr-2" />
                Process Return/Exchange
              </Button>
            </div>
            
            {/* Recent Returns Today */}
            {recentRefunds.length > 0 && (
              <div className="mt-4 pt-4 border-t text-left">
                <h3 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                  <RotateCcw size={16} className="text-amber-600" />
                  Today's Returns ({recentRefunds.length})
                </h3>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {recentRefunds.slice(0, 5).map((refund, idx) => (
                    <div key={idx} className="p-2 bg-amber-50 rounded text-sm border border-amber-100">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{refund.refundNumber}</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          refund.refundType === 'exchange' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {refund.refundType === 'exchange' ? 'Exchange' : 'Refund'}
                        </span>
                      </div>
                      <div className="flex justify-between text-slate-600">
                        <span>From: {refund.originalReceiptNumber}</span>
                        <span className="font-medium">₱{refund.refundAmount?.toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Z-Reading Dialog - Available on this screen */}
        <ZReadingDialog
          open={showZReadingDialog}
          onOpenChange={setShowZReadingDialog}
          zReadingDate={zReadingDate}
          setZReadingDate={setZReadingDate}
          loadZReading={loadZReading}
          loadingZReading={loadingZReading}
          zReadingData={zReadingData}
          compact={true}
        />

        {/* Open Shift Dialog */}
        <Dialog open={showOpenShiftDialog} onOpenChange={setShowOpenShiftDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Open New Shift</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleOpenShift} className="space-y-4">
              <div>
                <Label>Opening Cash (₱)</Label>
                <Input
                  type="number"
                  value={openShiftForm.openingCash}
                  onChange={e => setOpenShiftForm({...openShiftForm, openingCash: e.target.value})}
                  placeholder="0.00"
                  autoFocus
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={openShiftForm.notes}
                  onChange={e => setOpenShiftForm({...openShiftForm, notes: e.target.value})}
                  placeholder="Optional notes"
                  rows={2}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowOpenShiftDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                  Open Shift
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Main POS Interface
  return (
    <div className="h-[calc(100vh-120px)] flex flex-col">
      {/* Header - mobile responsive */}
      <div className="bg-emerald-600 text-white p-2 sm:p-3 rounded-t-xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <div>
          <h1 className="text-base sm:text-lg font-bold">NAS Point of Sale</h1>
          <p className="text-xs sm:text-sm text-emerald-100">
            Shift: {currentShift.shiftNumber} | {currentShift.cashierName}
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto justify-between sm:justify-end">
          <div className="text-left sm:text-right">
            <p className="text-xs text-emerald-100">Shift Sales</p>
            <p className="text-lg sm:text-xl font-bold">{formatCurrency(currentShift.totalSales || 0)}</p>
          </div>
          {/* Printer Info Badge - Shows Bluetooth status */}
          <button 
            onClick={() => setShowPrinterSetupDialog(true)}
            className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-colors cursor-pointer ${
              isBluetoothConnected 
                ? 'bg-blue-500/70 hover:bg-blue-400/70' 
                : 'bg-emerald-700/50 hover:bg-emerald-600/50'
            }`}
            title={isBluetoothConnected ? 'Bluetooth printer connected - Click for settings' : 'Click to connect printer'}
          >
            {isBluetoothConnected ? (
              <Bluetooth size={14} className="text-white" />
            ) : (
              <Printer size={14} />
            )}
            <span className="text-emerald-100 hidden sm:inline">
              {isBluetoothConnected ? `${printerSettings.printerName} ✓` : (printerSettings.printerName || PRINTER_CONFIG.model)}
            </span>
            {!isBluetoothConnected && printerSettings.isConfigured && <CheckCircle2 size={12} className="text-emerald-300" />}
          </button>
          {/* Z-Reading Button */}
          <Button 
            onClick={() => {
              setZReadingDate(new Date().toISOString().split('T')[0]);
              loadZReading(new Date().toISOString().split('T')[0]);
              setShowZReadingDialog(true);
            }}
            variant="outline"
            size="sm"
            className="border-white text-white hover:bg-emerald-700"
          >
            <BarChart3 size={16} className="mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Z-Reading</span>
            <span className="sm:hidden">Z</span>
          </Button>
          {/* Refund/Exchange Button */}
          <Button 
            onClick={() => {
              setShowRefundDialog(true);
              setRefundSearchTerm('');
              setRefundOriginalSale(null);
              setRefundItems([]);
              setRefundReason('');
              setRefundType('refund');
            }}
            variant="outline"
            size="sm"
            className="border-amber-300 text-amber-100 hover:bg-amber-600 bg-amber-500/30"
            data-testid="refund-btn"
          >
            <RotateCcw size={16} className="mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Returns</span>
            <span className="sm:hidden">Ret</span>
          </Button>
          <Button 
            onClick={() => setShowCloseShiftDialog(true)}
            variant="outline"
            size="sm"
            className="border-white text-white hover:bg-emerald-700"
          >
            <StopCircle size={16} className="mr-1 sm:mr-2" />
            <span className="hidden sm:inline">End Shift</span>
            <span className="sm:hidden">End</span>
          </Button>
        </div>
      </div>

      {/* Main Content - Improved for landscape and mobile */}
      <div className="flex-1 flex flex-col md:flex-row gap-2 sm:gap-4 p-2 sm:p-4 bg-slate-100 overflow-hidden">
        {/* Product Search & Results */}
        <div className="flex-1 flex flex-col overflow-hidden min-h-0 md:min-h-full">
          {/* Search */}
          <div className="relative mb-2 sm:mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
            <Input
              ref={searchRef}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search products..."
              className="pl-10 h-10 sm:h-12 text-base sm:text-lg"
              autoFocus
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X size={18} />
              </button>
            )}
          </div>

          {/* Quick Categories - Horizontal scroll on mobile */}
          <div className="flex gap-2 mb-2 sm:mb-4 overflow-x-auto pb-1 items-center">
            <Button
              variant={searchTerm === '' ? "default" : "outline"}
              size="sm"
              onClick={() => setSearchTerm('')}
              className={`whitespace-nowrap ${searchTerm === '' ? "bg-emerald-600" : "bg-white"}`}
            >
              All ({products.length})
            </Button>
            {['Feeds', 'Veterinary', 'Seeds', 'Fertilizer', 'Tools'].map(cat => (
              <Button
                key={cat}
                variant={searchTerm === cat ? "default" : "outline"}
                size="sm"
                onClick={() => setSearchTerm(searchTerm === cat ? '' : cat)}
                className={`whitespace-nowrap ${searchTerm === cat ? "bg-emerald-600" : "bg-white"}`}
              >
                {cat}
              </Button>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                loadProducts();
                toast.info('Refreshing products...');
              }}
              className="ml-auto whitespace-nowrap"
            >
              <RefreshCw size={14} className="mr-1" /> Refresh
            </Button>
          </div>

          {/* Products Grid - Better responsive for landscape */}
          <div className="flex-1 overflow-y-auto min-h-[150px]">
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
              {(searchTerm.length >= 1 ? searchResults : products).map(product => {
                const hasWholesale = product.wholesaleStock > 0;
                const hasRetail = product.retailStock > 0;
                const isOutOfStock = !hasWholesale && !hasRetail;
                
                return (
                  <div
                    key={product.id}
                    className={`bg-white rounded-lg border p-3 ${isOutOfStock ? 'opacity-50' : 'hover:border-emerald-300 hover:shadow-md'} transition-all`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <p className="font-medium text-slate-900 text-sm leading-tight">{product.name}</p>
                        <p className="text-xs text-slate-400">{product.category}</p>
                      </div>
                      {isOutOfStock && (
                        <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">Out of Stock</span>
                      )}
                    </div>
                    
                    {/* Stock Info */}
                    <div className="flex gap-2 mb-2 text-xs">
                      <div className={`flex-1 p-1.5 rounded ${hasWholesale ? 'bg-blue-50' : 'bg-slate-50'}`}>
                        <span className="text-slate-500">W:</span>
                        <span className={`ml-1 font-semibold ${hasWholesale ? 'text-blue-600' : 'text-slate-400'}`}>
                          {product.wholesaleStock || 0} {getWholesaleUnit(product).plural}
                        </span>
                      </div>
                      <div className={`flex-1 p-1.5 rounded ${hasRetail ? 'bg-purple-50' : 'bg-slate-50'}`}>
                        <span className="text-slate-500">R:</span>
                        <span className={`ml-1 font-semibold ${hasRetail ? 'text-purple-600' : 'text-slate-400'}`}>
                          {product.retailStock || 0} {getRetailUnit(product).plural}
                        </span>
                      </div>
                    </div>
                    
                    {/* Prices */}
                    <div className="flex gap-2 mb-2 text-xs">
                      <div className="flex-1 text-center">
                        <span className="text-blue-600 font-bold">{formatCurrency(product.wholesalePrice || product.sellingPricePerSack)}</span>
                        <span className="text-slate-400 ml-1">/{getWholesaleUnit(product).singular}</span>
                      </div>
                      <div className="flex-1 text-center">
                        <span className="text-purple-600 font-bold">{formatCurrency(product.retailPrice || product.sellingPricePerKg)}</span>
                        <span className="text-slate-400 ml-1">/{getRetailUnit(product).singular}</span>
                      </div>
                    </div>
                    
                    {/* Select Button */}
                    <button
                      type="button"
                      onClick={() => openProductSelect(product)}
                      disabled={isOutOfStock}
                      data-testid={`select-product-${product.id}`}
                      className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                        isOutOfStock 
                          ? 'bg-slate-100 text-slate-400 cursor-not-allowed' 
                          : 'bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800'
                      }`}
                    >
                      {isOutOfStock ? 'Out of Stock' : 'Select'}
                    </button>
                  </div>
                );
              })}
              
              {products.length === 0 && (
                <div className="col-span-full flex items-center justify-center py-12 text-slate-400">
                  <div className="text-center">
                    <Package size={48} className="mx-auto mb-4 opacity-50" />
                    <p>No products available</p>
                    <p className="text-sm">Add products in NAS Products page first</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Cart Panel - Fixed width on desktop, collapsible on mobile */}
        <div className="w-full md:w-80 lg:w-96 bg-white rounded-lg shadow-lg flex flex-col max-h-[40vh] md:max-h-full overflow-hidden">
          {/* Cart Header with item count */}
          <div className="p-3 border-b flex justify-between items-center bg-emerald-50">
            <h2 className="font-bold text-base flex items-center gap-2">
              <ShoppingCart size={18} />
              Cart
              {cart.length > 0 && (
                <Badge className="bg-emerald-600">{cart.length} items</Badge>
              )}
            </h2>
            {cart.length > 0 && (
              <Button variant="ghost" size="sm" onClick={clearCart} className="text-red-500 h-8">
                <Trash2 size={14} className="mr-1" />
                Clear
              </Button>
            )}
          </div>

          {/* Cart Items - Scrollable with visible scroll indicator */}
          <div className="flex-1 overflow-y-auto p-2 relative min-h-[100px]">
            {cart.length === 0 ? (
              <div className="text-center text-slate-400 py-6">
                <ShoppingCart size={28} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">Cart is empty</p>
              </div>
            ) : (
              <>
                {/* Scroll indicator - shows when there are more items */}
                {cart.length > 3 && (
                  <div className="sticky top-0 left-0 right-0 text-center text-xs text-slate-400 bg-gradient-to-b from-white to-transparent py-1 z-10">
                    ↑ Scroll to see all {cart.length} items ↓
                  </div>
                )}
                {cart.map((item, index) => (
                  <div key={index} className="p-2 bg-slate-50 rounded-lg mb-2 border border-slate-100">
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{item.productName}</p>
                        <p className="text-xs text-slate-500">{item.quantity} {item.unit} × {formatCurrency(item.unitPrice)}</p>
                      </div>
                      <p className="font-bold text-emerald-600 whitespace-nowrap">{formatCurrency(item.totalPrice)}</p>
                      <button
                        onClick={() => removeFromCart(index)}
                        className="text-red-400 hover:text-red-600 p-1"
                      >
                        <X size={14} />
                      </button>
                    </div>
                    {/* Quick quantity adjust */}
                    <div className="flex items-center gap-1 mt-1">
                      <button
                        onClick={() => updateCartItem(index, 'quantity', Math.max(0.1, item.quantity - 1))}
                        className="p-1 bg-slate-200 rounded hover:bg-slate-300"
                      >
                        <Minus size={12} />
                      </button>
                      <span className="text-xs px-2">{item.quantity}</span>
                      <button
                        onClick={() => updateCartItem(index, 'quantity', item.quantity + 1)}
                        className="p-1 bg-slate-200 rounded hover:bg-slate-300"
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>

          {/* Cart Summary - Always visible */}
          <div className="border-t p-3 bg-white">
            <div className="flex justify-between mb-1 text-sm">
              <span className="text-slate-600">Subtotal ({cart.length} items)</span>
              <span className="font-medium">{formatCurrency(subtotal)}</span>
            </div>
            <div className="flex justify-between mb-3 text-xl font-bold">
              <span>Total</span>
              <span className="text-emerald-600">{formatCurrency(subtotal)}</span>
            </div>
            <Button
              onClick={() => setShowCheckoutDialog(true)}
              disabled={cart.length === 0}
              className="w-full bg-emerald-600 hover:bg-emerald-700 h-10 text-base"
            >
              <CreditCard size={18} className="mr-2" />
              Checkout
            </Button>
          </div>
        </div>
      </div>

      {/* Checkout Dialog */}
      <Dialog open={showCheckoutDialog} onOpenChange={setShowCheckoutDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Checkout</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-emerald-50 rounded-lg text-center">
              <p className="text-sm text-emerald-700">Total Amount</p>
              <p className="text-3xl font-bold text-emerald-800">{formatCurrency(total)}</p>
            </div>

            <div>
              <Label>Payment Method</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {[
                  { value: 'cash', label: 'Cash', icon: Banknote },
                  { value: 'gcash', label: 'GCash', icon: Smartphone },
                  { value: 'credit', label: 'Credit', icon: CreditCard }
                ].map(method => (
                  <button
                    key={method.value}
                    onClick={() => setCheckoutForm({...checkoutForm, paymentMethod: method.value})}
                    className={`p-3 rounded-lg border-2 flex flex-col items-center gap-1 ${
                      checkoutForm.paymentMethod === method.value
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <method.icon size={24} className={checkoutForm.paymentMethod === method.value ? 'text-emerald-600' : 'text-slate-400'} />
                    <span className="text-sm font-medium">{method.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {checkoutForm.paymentMethod === 'cash' && (
              <div>
                <Label>Amount Received (₱)</Label>
                <Input
                  type="number"
                  value={checkoutForm.amountReceived}
                  onChange={e => setCheckoutForm({...checkoutForm, amountReceived: e.target.value})}
                  placeholder="0.00"
                  className="text-lg h-12"
                  autoFocus
                />
                {amountReceived > 0 && amountReceived >= total && (
                  <p className="text-lg font-bold text-emerald-600 mt-2">
                    Change: {formatCurrency(change)}
                  </p>
                )}
              </div>
            )}

            <div>
              <Label>Customer Name</Label>
              <Input
                value={checkoutForm.customerName}
                onChange={e => setCheckoutForm({...checkoutForm, customerName: e.target.value})}
                placeholder="Walk-in"
              />
            </div>

            <div>
              <Label>Discount (₱)</Label>
              <Input
                type="number"
                value={checkoutForm.discount}
                onChange={e => setCheckoutForm({...checkoutForm, discount: e.target.value})}
                placeholder="0.00"
              />
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button type="button" variant="outline" onClick={() => setShowCheckoutDialog(false)}>
                Cancel
              </Button>
              <Button onClick={() => handleCheckout(false)} variant="secondary" className="bg-slate-200 hover:bg-slate-300">
                <Receipt size={18} className="mr-2" />
                Save Only
              </Button>
              <Button onClick={() => handleCheckout(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <Printer size={18} className="mr-2" />
                Save & Print
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      <Dialog open={showReceiptDialog} onOpenChange={setShowReceiptDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Sale Complete!
              <span className="text-xs font-normal text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                {PRINTER_CONFIG.brand} {PRINTER_CONFIG.model}
              </span>
            </DialogTitle>
          </DialogHeader>
          {lastReceipt && (
            <div className="space-y-4 print:block" id="receipt">
              <div className="receipt-header text-center border-b pb-4">
                <h3 className="font-bold text-lg">Nenen's Agrivet Supply</h3>
                <p className="text-sm text-slate-500">{lastReceipt.receiptNumber}</p>
                <p className="text-sm text-slate-500">{formatDateTime(lastReceipt.createdAt)}</p>
              </div>
              
              <div className="receipt-items space-y-2">
                {lastReceipt.items?.map((item, idx) => (
                  <div key={idx} className="receipt-item flex justify-between text-sm">
                    <span className="receipt-item-name">{item.productName} x{item.quantity} {item.unit}</span>
                    <span className="receipt-item-price">{formatCurrency(item.totalPrice)}</span>
                  </div>
                ))}
              </div>

              <div className="receipt-totals border-t pt-2 space-y-1">
                <div className="receipt-row flex justify-between">
                  <span>Subtotal</span>
                  <span>{formatCurrency(lastReceipt.subtotal)}</span>
                </div>
                {lastReceipt.discount > 0 && (
                  <div className="receipt-row flex justify-between text-red-600">
                    <span>Discount</span>
                    <span>-{formatCurrency(lastReceipt.discount)}</span>
                  </div>
                )}
                <div className="receipt-total flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span>{formatCurrency(lastReceipt.totalAmount)}</span>
                </div>
                {lastReceipt.paymentMethod === 'cash' && (
                  <>
                    <div className="receipt-row flex justify-between">
                      <span>Cash</span>
                      <span>{formatCurrency(lastReceipt.amountReceived)}</span>
                    </div>
                    <div className="receipt-row flex justify-between">
                      <span>Change</span>
                      <span>{formatCurrency(lastReceipt.change)}</span>
                    </div>
                  </>
                )}
              </div>

              <div className="receipt-footer text-center text-sm text-slate-500 pt-2 border-t">
                <p>Payment: {lastReceipt.paymentMethod?.toUpperCase()}</p>
                <p>Cashier: {lastReceipt.cashierName}</p>
                <p className="mt-2">Thank you for your purchase!</p>
                
                {/* Disclaimer */}
                <div className="mt-3 pt-2 border-t border-dashed text-xs text-slate-400">
                  <p className="font-semibold">NOT AN OFFICIAL RECEIPT</p>
                  <p>For inventory purposes only.</p>
                  <p>For Official Receipt, please</p>
                  <p>speak to one of our staff.</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowReceiptDialog(false)}>
              Close
            </Button>
            {isBluetoothConnected ? (
              <Button 
                onClick={() => lastReceipt && printViaBluetooth(lastReceipt)} 
                className="bg-blue-600 hover:bg-blue-700"
                disabled={isPrinting}
              >
                {isPrinting ? (
                  <><Loader2 size={18} className="mr-2 animate-spin" /> Printing...</>
                ) : (
                  <><Bluetooth size={18} className="mr-2" /> Print (Bluetooth)</>
                )}
              </Button>
            ) : (
              <Button onClick={printReceipt} className="bg-emerald-600 hover:bg-emerald-700">
                <Printer size={18} className="mr-2" />
                Print Receipt
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Shift Dialog */}
      <Dialog open={showCloseShiftDialog} onOpenChange={setShowCloseShiftDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>End Shift</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCloseShift} className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex justify-between mb-2">
                <span className="text-slate-600">Opening Cash</span>
                <span className="font-medium">{formatCurrency(currentShift.openingCash)}</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-slate-600">Cash Sales</span>
                <span className="font-medium">{formatCurrency(currentShift.cashSales || 0)}</span>
              </div>
              <div className="flex justify-between font-bold text-lg border-t pt-2">
                <span>Expected Cash</span>
                <span className="text-emerald-600">
                  {formatCurrency((currentShift.openingCash || 0) + (currentShift.cashSales || 0))}
                </span>
              </div>
            </div>

            <div>
              <Label>Closing Cash Count (₱)</Label>
              <Input
                type="number"
                value={closeShiftForm.closingCash}
                onChange={e => setCloseShiftForm({...closeShiftForm, closingCash: e.target.value})}
                placeholder="Count your cash drawer"
                autoFocus
              />
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={closeShiftForm.notes}
                onChange={e => setCloseShiftForm({...closeShiftForm, notes: e.target.value})}
                placeholder="Any notes about this shift"
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCloseShiftDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-red-600 hover:bg-red-700">
                <StopCircle size={18} className="mr-2" />
                End Shift
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Shift Report Dialog */}
      <ShiftReportDialog
        open={showShiftReportDialog}
        onOpenChange={setShowShiftReportDialog}
        shiftReport={shiftReport}
      />
      
      {/* Product Select Dialog */}
      <ProductSelectDialog
        open={showProductSelectDialog}
        onOpenChange={setShowProductSelectDialog}
        selectedProduct={selectedProduct}
        productSelectForm={productSelectForm}
        setProductSelectForm={setProductSelectForm}
        handleProductSelectAdd={handleProductSelectAdd}
      />

      {/* Printer Setup Dialog */}
      <PrinterSetupDialog
        open={showPrinterSetupDialog}
        onOpenChange={setShowPrinterSetupDialog}
        printerSettings={printerSettings}
        setPrinterSettings={setPrinterSettings}
        savePrinterSettings={savePrinterSettings}
        isBluetoothConnected={isBluetoothConnected}
        isBluetoothSupported={isBluetoothSupported}
        isConnecting={isConnecting}
        isPrinting={isPrinting}
        connectBluetoothPrinter={connectBluetoothPrinter}
        disconnectBluetoothPrinter={disconnectBluetoothPrinter}
        printViaBluetooth={printViaBluetooth}
        lastReceipt={lastReceipt}
      />

      {/* Z-Reading Dialog */}
      <ZReadingDialog
        open={showZReadingDialog}
        onOpenChange={setShowZReadingDialog}
        zReadingDate={zReadingDate}
        setZReadingDate={setZReadingDate}
        loadZReading={loadZReading}
        loadingZReading={loadingZReading}
        zReadingData={zReadingData}
      />

      {/* Refund/Exchange Dialog */}
      <RefundDialog
        open={showRefundDialog}
        onOpenChange={setShowRefundDialog}
        refundSearchTerm={refundSearchTerm}
        setRefundSearchTerm={setRefundSearchTerm}
        searchSaleForRefund={searchSaleForRefund}
        loadingRefund={loadingRefund}
        refundOriginalSale={refundOriginalSale}
        refundType={refundType}
        setRefundType={setRefundType}
        refundItems={refundItems}
        updateRefundItemQuantity={updateRefundItemQuantity}
        refundReason={refundReason}
        setRefundReason={setRefundReason}
        processRefund={processRefund}
      />

      {/* Refund Success Dialog */}
      <RefundSuccessDialog
        open={showRefundSuccess}
        onOpenChange={setShowRefundSuccess}
        lastRefund={lastRefund}
      />
    </div>
  );
};

export default NASPOS;
